/**
 * Plan Create Page
 * Wizard for creating new waste management plans
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PlanWizard } from '../components/plan/PlanWizard';
import { planService } from '../services/plan.service';
import type { PlanData } from '../types';
import { TrustIndicator } from '../components/common/TrustIndicator';
import { generatePlanPDF, downloadPDF } from '../utils/pdfGenerator';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { toast } from '@/lib/utils/toast';

export function PlanCreate() {
  const { id } = useParams<{ id: string }>();
  const [createdPlanUrl, setCreatedPlanUrl] = useState<string | null>(null);
  const [createdPlanId, setCreatedPlanId] = useState<string | null>(null);
  const [createdPlanEmail, setCreatedPlanEmail] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [resendEmailSuccess, setResendEmailSuccess] = useState(false);
  const [resendEmailError, setResendEmailError] = useState<string | null>(null);

  const handleSubmit = async (planData: PlanData) => {
    setIsSubmitting(true);
    try {
      const applicantEmail = planData.applicant_info.contact_email;
      const response = await planService.createPlan(planData, applicantEmail);

      if (response) {
        // Build the full URL for the plan
        const baseUrl = window.location.origin;
        const fullPlanUrl = `${baseUrl}/plans/${response.plan.id}`;
        setCreatedPlanUrl(fullPlanUrl);
        setCreatedPlanId(response.plan.id);
        setCreatedPlanEmail(applicantEmail);
      }
    } catch (error) {
      console.error('Failed to create plan:', error);
      toast.error('Failed to create plan. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendEmail = async () => {
    if (!createdPlanId) return;

    setIsResendingEmail(true);
    setResendEmailError(null);
    setResendEmailSuccess(false);

    try {
      await planService.resendConfirmationEmail(createdPlanId);
      setResendEmailSuccess(true);
      setTimeout(() => setResendEmailSuccess(false), 5000);
    } catch (error) {
      console.error('Failed to resend email:', error);
      setResendEmailError('Failed to send email. Please try again.');
      setTimeout(() => setResendEmailError(null), 5000);
    } finally {
      setIsResendingEmail(false);
    }
  };

  const handleCopyUrl = () => {
    if (createdPlanUrl) {
      navigator.clipboard.writeText(createdPlanUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleDownloadPDF = async () => {
    if (!createdPlanId || !createdPlanUrl) {
      toast.error('Plan information not available');
      return;
    }

    setIsGeneratingPDF(true);
    try {
      // Fetch the full plan data from the API
      const plan = await planService.getPlan(createdPlanId);

      // Generate PDF
      const pdfBlob = await generatePlanPDF(plan, createdPlanUrl);

      // Trigger download
      const filename = `waste-management-plan-${createdPlanId}.pdf`;
      downloadPDF(pdfBlob, filename);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Show success screen with plan URL after creation
  if (createdPlanUrl) {
    return (
      <ErrorBoundary resetKeys={[]}>
        <div className="max-w-3xl mx-auto py-8">
        <div className="card text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-10 h-10 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          {/* Heading */}
          <h1 className="text-4xl font-bold text-neutral-900 mb-3">
            Your Plan is Ready!
          </h1>

          <p className="text-lg text-neutral-600 mb-8 max-w-xl mx-auto">
            Your plan is ready to use! We&apos;ve sent you an email with your unique access link. You can also download a PDF or bookmark this page.
          </p>

          {/* Plan URL Card - Prominent Display */}
          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6 mb-6">
            <label className="block text-sm font-semibold text-primary-900 mb-3 text-left">
              Your Plan URL
            </label>
            <div className="bg-white rounded-lg shadow-sm border-2 border-primary-200">
              <div className="flex items-center gap-3 p-4">
                <div className="flex-1 min-w-0">
                  <code className="text-sm md:text-base text-neutral-900 font-mono break-all block">
                    {createdPlanUrl}
                  </code>
                </div>
                <button
                  onClick={handleCopyUrl}
                  className="btn-secondary whitespace-nowrap flex items-center gap-2 flex-shrink-0"
                  aria-label="Copy plan URL"
                >
                  {copySuccess ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Email Confirmation Section */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6 text-left">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <div className="flex-1">
                <h3 className="font-semibold text-green-900 mb-2">
                  Confirmation Email Sent!
                </h3>
                <p className="text-green-800 mb-3">
                  We&apos;ve sent a confirmation email to <strong>{createdPlanEmail}</strong> with your plan access link and instructions.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 items-start">
                  <button
                    onClick={handleResendEmail}
                    disabled={isResendingEmail || resendEmailSuccess}
                    className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isResendingEmail ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Sending...
                      </>
                    ) : resendEmailSuccess ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Email Sent!
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Resend Email
                      </>
                    )}
                  </button>
                  {resendEmailError && (
                    <p className="text-sm text-red-600">{resendEmailError}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Security Warning */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-left">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">
                  Keep Your Plan Safe
                </h3>
                <p className="text-blue-800 mb-3">
                  This unique link is your key to access and edit your plan. We recommend saving it in multiple places:
                </p>
                <ul className="space-y-2 text-blue-800">
                  <li className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Bookmark in your browser
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Check your email (we sent you a copy)
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Save to your project files
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
            <button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              className="btn-primary flex items-center justify-center gap-2 px-8 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGeneratingPDF ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Generating PDF...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download PDF
                </>
              )}
            </button>
            <a
              href={createdPlanUrl}
              className="btn-secondary px-8 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View Your Plan
            </a>
          </div>

          {/* Secondary Action */}
          <div className="pt-6 border-t border-neutral-200">
            <a
              href="/"
              className="text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Another Plan
            </a>
          </div>
        </div>
      </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary resetKeys={[]}>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">Create Waste Management Plan</h1>
          <p className="text-neutral-600">
            Follow the steps below to create a CalGreen 5.408 compliant waste management plan
          </p>
        </div>

        {/* Trust Indicator - Build confidence before starting */}
        <div className="mb-8">
          <TrustIndicator />
        </div>

        <PlanWizard onSubmit={handleSubmit} existingPlanId={id} isSubmitting={isSubmitting} />
      </div>
    </ErrorBoundary>
  );
}
